// src/pages/NotificationManagement/components/NotificationListItem.tsx
// Một dòng thông báo trong danh sách, có chỉ báo đã đọc/chưa đọc
// Theo mục 3.3 và 6 (accessibility) của .features/thong-bao/ke_hoach_ui_man_thong_bao.md

import React from 'react';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { NotificationItem } from 'slices/notifications/types';
import { getNotificationTypeMeta, formatFullDateTime } from 'Components/Common/Notification/notificationMeta';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('notification_management');

interface NotificationListItemProps {
  item: NotificationItem;
  onClick: () => void;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({ item, onClick }) => {
  const location = useLocation();
  const meta = getNotificationTypeMeta(item.type);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const isUnread = !item.isRead;

  if (!PageActionPermission.canDisplay(location.pathname, 'button-view-detail')) return null;

  return (
    <div
      className="d-flex align-items-start gap-3 p-3 border-bottom"
      role="button"
      tabIndex={0}
      aria-label={isUnread ? st('title_chua_doc_text', { title: item.title }) : st('title_da_doc_text', { title: item.title })}
      data-action-code="button-view-detail"
      data-action-name="Xem chi tiết thông báo"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{
        cursor: 'pointer',
        backgroundColor: isUnread ? '#eff6ff' : 'transparent',
        borderLeft: isUnread ? '4px solid #2563eb' : '4px solid transparent',
      }}
    >
      <div className="pt-2 flex-shrink-0" style={{ width: 8 }}>
        {isUnread && (
          <span
            className="d-inline-block rounded-circle"
            style={{ width: 8, height: 8, backgroundColor: '#2563eb' }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="avatar-xs flex-shrink-0" style={{ opacity: isUnread ? 1 : 0.55 }}>
        <span className="avatar-title rounded-circle fs-18" style={{ backgroundColor: meta.iconBg, color: meta.iconColor }}>
          <i className={meta.icon}></i>
        </span>
      </div>

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h6 className={classnames('mb-1', isUnread ? 'fw-semibold' : 'fw-normal text-muted')}>
            {item.title}
            {isUnread && <span className="visually-hidden"> {st('chua_doc_text')}</span>}
          </h6>
          <small className="text-muted text-nowrap">{formatFullDateTime(item.createdAt)}</small>
        </div>

        <p
          className={classnames('mb-2 fs-13', isUnread ? 'text-body' : 'text-muted')}
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {item.content}
        </p>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span
            className="badge"
            style={{
              backgroundColor: meta.badgeBg,
              color: meta.badgeColor,
              opacity: isUnread ? 1 : 0.7,
            }}
          >
            {meta.label}
          </span>
          {item.sender && <small className="text-muted">{st('tu_hint')} {item.sender}</small>}
          {isUnread ? (
            <span className="badge bg-primary-subtle text-primary ms-auto">{st('chua_doc_text_2')}</span>
          ) : (
            <span className="badge bg-light text-muted ms-auto">
              <i className="ri-check-double-line align-middle me-1"></i>{st('da_doc_text')}
            </span>
          )}
        </div>

        {item.isRead && item.readAt && (
          <div className="mt-1">
            <small className="text-muted fst-italic">{st('da_doc_luc_hint')} {formatFullDateTime(item.readAt)}</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationListItem;
